import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AnimatedTitle from "@/components/feature/AnimatedTitle";
import BoldDiagonalRed from "@/components/feature/BoldDiagonalRed";
import DiagonalLines from "@/components/feature/DiagonalLines";


const voices = [
  {
    text: "広告費を抑えても新規のお客様が来てくれる。ホームページとSEOで集客の仕組みが変わりました。",
    name: "美容サロン・オーナー",
    tags: ["#美容サロン", "#30代女性"],
    icon: "ri-scissors-line",
    result: "問合 +3倍",
  },
  {
    text: "MEO対策がこんなに効くとは思っていなかった。Googleマップ上位表示で患者様が明らかに増えました。",
    name: "整骨院・院長",
    tags: ["#整骨院", "#40代男性"],
    icon: "ri-map-pin-2-line",
    result: "来院 +2倍",
  },
  {
    text: "最初からターゲットや競合の話をしてくれた。売上のためのサイトという視点が全然違いました。",
    name: "飲食店・オーナー",
    tags: ["#飲食店", "#50代男性"],
    icon: "ri-restaurant-line",
    result: "売上 +68%",
  },
  {
    text: "インフルエンサーとのタイアップから広告運用まで全部お任せ。予約がしばらく埋まるほど反響がありました。",
    name: "ネイルサロン・オーナー",
    tags: ["#ネイルサロン", "#20代女性"],
    icon: "ri-brush-line",
    result: "予約 満員",
  },
  {
    text: "ホームページ・SEO・広告をまとめてお願いしたことで、一貫した戦略で動けました。問い合わせが倍近くに。",
    name: "リフォーム会社・代表",
    tags: ["#リフォーム", "#50代男性"],
    icon: "ri-home-gear-line",
    result: "問合 +1.9倍",
  },
  {
    text: "以前の制作会社は問い合わせが1件も来なかった。クラプリックスは集客を見据えた設計で動いてくれるので全然違います。",
    name: "塗装屋・代表",
    tags: ["#塗装", "#40代男性"],
    icon: "ri-paint-brush-line",
    result: "集客 改善",
  },
];

const CARD_WIDTH = 300;
const GAP = 24;

function VoiceCard({ v, idx }: { v: typeof voices[0]; idx: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), 80);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="voice-card-hover relative flex flex-col overflow-hidden cursor-default shrink-0"
      style={{
        width: `${CARD_WIDTH}px`,
        minWidth: "240px",
        background: "#ffffff",
        border: "1px solid #e8e8e8",
        padding: "16px 16px",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
        transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${idx * 0.08}s, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${idx * 0.08}s`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top accent line */}
      <div
        className="absolute top-0 left-0 right-0 h-0.5"
        style={{
          background: "#c00000",
          transform: hovered ? "scaleX(1)" : "scaleX(0)",
          transformOrigin: "left",
          transition: "transform 0.45s cubic-bezier(0.22,1,0.36,1)",
        }}
      />

      {/* Big quote mark */}
      <div
        className="absolute top-4 right-5 select-none pointer-events-none"
        style={{
          fontFamily: "'Barlow', sans-serif",
          fontWeight: 700,
          fontSize: "80px",
          lineHeight: 1,
          color: hovered ? "rgba(192,0,0,0.08)" : "rgba(0,0,0,0.04)",
          transition: "color 0.4s ease",
        }}
      >
        "
      </div>

      {/* Icon */}
      <div className="flex items-center justify-between mb-5">
        <div
          className="w-10 h-10 flex items-center justify-center"
          style={{
            background: hovered ? "#c00000" : "#f5f5f5",
            transition: "background 0.4s ease",
          }}
        >
          <i
            className={`${v.icon} text-base`}
            style={{ color: hovered ? "#ffffff" : "#111111", transition: "color 0.4s ease" }}
          />
        </div>
      </div>

      {/* Text */}
      <p
        className="flex-1 leading-loose mb-6"
        style={{
          fontFamily: "'Noto Sans JP', sans-serif",
          fontSize: "clamp(14px, 1.2vw, 15px)",
          color: "#111111",
          lineHeight: 1.7,
        }}
      >
        {v.text}
      </p>

      {/* Divider */}
      <div style={{ height: "1px", background: "#f0f0f0", marginBottom: "16px" }} />

      {/* Name + tags */}
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "#f0f0f0" }}
        >
          <i className="ri-user-3-line text-sm" style={{ color: "#111111" }} />
        </div>
        <div className="flex flex-col gap-1">
          <p
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              fontSize: "15px",
              fontWeight: 700,
              color: "#111111",
              lineHeight: 1,
            }}
          >
            {v.name}
          </p>
          <div className="flex gap-2">
            {v.tags.map((tag, ti) => (
              <span
                key={ti}
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: "12px",
                  color: "#111111",
                  lineHeight: 1,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VoiceSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const dragStart = useRef<{ x: number; scrollLeft: number } | null>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.05, rootMargin: "0px 0px -50px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Drag to scroll
  const onMouseDown = (e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    setIsDragging(false);
    dragStart.current = { x: e.clientX, scrollLeft: el.scrollLeft };
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragStart.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const dx = e.clientX - dragStart.current.x;
    if (Math.abs(dx) > 4) setIsDragging(true);
    el.scrollLeft = dragStart.current.scrollLeft - dx;
  };

  const onMouseUp = () => {
    dragStart.current = null;
    setTimeout(() => setIsDragging(false), 50);
  };

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setScrollProgress(max > 0 ? el.scrollLeft / max : 0);
  };

  return (
    <section
      ref={sectionRef}
      className="relative"
      style={{ background: "linear-gradient(to right, #e8ddd8, #ffffff)", borderTop: "1px solid #e8e8e8" }}
    >
      {/* Bold diagonal red lines — full width accent */}
      <BoldDiagonalRed pattern="full" />


      {/* Diagonal decorative lines */}
      <DiagonalLines variant="gold" intensity="light" />

      {/* Section header */}
      <div className="relative z-10 px-4 md:px-12 lg:px-20 pt-20 md:pt-32 pb-8 md:pb-14">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10 md:mb-16">
          <div>
            <AnimatedTitle
              outlineText="Real"
              filledText="Voice"
              stacked
              delay={0}
              labelTag="TESTIMONIALS"
            />
            <p
              className="mt-4 md:mt-6 max-w-3xl"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                fontSize: "15px",
                fontWeight: 500,
                color: "#111111",
                lineHeight: 1.8,
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(12px)",
                transition: "opacity 0.7s ease 0.3s, transform 0.7s cubic-bezier(0.22,1,0.36,1) 0.3s",
              }}
            >
              サイトが完成してからも、お客様とCRUPLIXとの関係は続いていきます。嬉しいお言葉はもちろん、率直なご指摘やご要望も、真摯に受け止めながら、より良いWEB制作を目指し続けています。
            </p>
          </div>
        </div>
      </div>

      {/* Horizontal scroll area */}
      <div
        ref={scrollRef}
        className="pb-16 md:pb-24"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onScroll={onScroll}
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          cursor: isDragging ? "grabbing" : "grab",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
          paddingLeft: "clamp(16px, 5vw, 80px)",
          paddingRight: "clamp(16px, 5vw, 80px)",
        }}
      >
        <div
          className="flex"
          style={{
            gap: `${GAP}px`,
            width: "max-content",
            userSelect: "none",
          }}
        >
          {voices.map((v, idx) => (
            <VoiceCard key={idx} v={v} idx={idx} />
          ))}
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 md:px-12 lg:px-20 pb-16 md:pb-28">
        <div
          style={{
            height: "1px",
            background: "#e0e0e0",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: "100%",
              width: `${scrollProgress * 100}%`,
              background: "#c00000",
              transition: "width 0.1s ease",
            }}
          />
        </div>
      </div>
    </section>
  );
}